
import {
    FC, useState, useEffect,
    useContext,
    useMemo,
    useCallback,
} from 'react';
import LoaderImage from '@assets/loader.gif';
import Leaf from '@assets/leaf.png';
import * as types from '@models/types';
import * as url from '@api/requests';
import Table from '@components/Table';
import ColumnChart from '@components/Charts/ColumnChart';
import LineChart from '@components/Charts/LineChart';
import ExportButton from '@components/ExportButton';
import TraceableSankyDiagram from '@components/Diagrams/TraceableSankyDiagram';
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import BoltIcon from '@mui/icons-material/Bolt';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import HeatMapChart from '@components/HeatMapChart';
import { OccupationsContext } from '@providers/Occupations';
import { getMostRequiredISCO4JobInfo, getJobPostsInfo, exportISCO4 } from '@api/requests';
import TopSkills from "@components/CountryDetails/components/TopSkills";
import Button from '@components/Button';
import { TextField } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import { ArrowRight, ArrowCircleDown } from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import RepeatIcon from '@mui/icons-material/Repeat';
import AppsIcon from '@mui/icons-material/Apps';
import LockIcon from '@mui/icons-material/Lock';
import InfoIcon from '@mui/icons-material/Info';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import { SkillTypes, WSSkillGroups } from '@helpers/enums';
import './OccupationTab.scss';

type OccupationTabProps = {
    countryData: types.CountryDataTypes;
    selectedProfile: types.SelectedProfileTypes;
    titles: string;
    isLoading: number
};

const OccupationTab: FC<OccupationTabProps> = ({
    countryData,
    selectedProfile,
    titles,
    isLoading }) => {
    const {
        mostRequiredISCO4JobInfo, setMostRequiredISCO4JobInfo,
        topNEmployers,
        mostRequiredJobInfo, setMostRequiredJobInfo,
        ISCO3OccupationList, setISCO3OccupationList,
        sectorAndOccupationInfo,
        topOccupationsBySectors,
        yearlyOccupationTrend,
        topSkills,
        totalJobPosts,
        memberMigrationSectors,
        memberMigrationOccupations
    } = useContext(OccupationsContext);

    
    const [yearlyPercentSentence, setYearlyPercentSentence] = useState(""); 
    const [isOccupationLoading, setIsOccupationLoading] = useState(false);
    const [occupationISCO3selected, setOccupationISCO3selected] = useState("");
    const [occupationISCO4selected, setOccupationISCO4selected] = useState("");
    const [isLoadingExportJobPosts, setIsLoadingExportJobPosts] = useState(false);
    const [isLoadingExportISCO4, setIsLoadingExportISCO4] = useState(false);
    const [percentageJobPosts, setPercentageJobPosts] = useState<string>("0");
    const [isco3OccupationListOrderByRanking, setISCO3OccupationListOrderByRanking] = useState<types.SelectedProfileChildrenTypes[]>([]);
    const [filteredISCO3OccupationListOrderByRanking, setFilteredISCO3OccupationListOrderByRanking] = useState<types.SelectedProfileChildrenTypes[]>([]);
    const [showHeatMapMigrationSectors, setShowHeatMapMigrationSectors] = useState(true);
    const [showHeatMapMigrationOccupations, setShowHeatMapMigrationOccupations] = useState(true);
    const [migrationSectorsHeatMap, setMigrationSectorsHeatMap] = useState<any[]>([]);
    const [migrationOccupationsHeatMap, setMigrationOccupationsHeatMap] = useState<any[]>([]);
    const [sectorMigrationHeatMapXAxisLabels, setSectorMigrationHeatMapXAxisLabels] = useState<any[]>([]);
    const [sectorMigrationHeatMapYAxisLabels, setSectorMigrationHeatMapYAxisLabels] = useState<any[]>([]);
    const [occupationMigrationHeatMapXAxisLabels, setOccupationMigrationHeatMapXAxisLabels] = useState<any[]>([]);
    const [occupationMigrationHeatMapYAxisLabels, setOccupationMigrationHeatMapYAxisLabels] = useState<any[]>([]);
    const [topSkillGroup, setTopSkillGroup] = useState<string>(WSSkillGroups.HardSkill);
    const [skillType, setSkillType] = useState<string>(SkillTypes.ESCO);
    const [isco3Search, setISCO3Search] = useState<string>("");
    const [showMostRequiredISCO4JobInfo, setShowMostRequiredISCO4JobInfo] = useState(true);
    const [totalRanking, setTotalRanking] = useState<number>(0);
    const [currentRanking, setCurrentRanking] = useState<number>(0);
    const [iSCO4OccupationAiList, setISCO4OccupationAiList] = useState([]);
    const [isco4AIChartDataList, setIsco4AIChartDataList] = useState([]);
    const [aiChartIsco4SelectedList, setAiChartIsco4SelectedList] = useState<string[]>(['']);
    const [isLoadingIsco4AIData, setIsLoadingIsco4AIData] = useState(false);

    useEffect(() => {
        setMostRequiredJobInfo && setMostRequiredJobInfo({ education_level: "", specializations: [], experience_range: "", total_jobs: 0, isco3_job_counts: [] });
        setISCO3OccupationList && setISCO3OccupationList([]);
        setMostRequiredISCO4JobInfo && setMostRequiredISCO4JobInfo(null);
        setPercentageJobPosts("0");
        setISCO3OccupationListOrderByRanking([]);
        setFilteredISCO3OccupationListOrderByRanking([]);
        setShowHeatMapMigrationSectors(true);
        setShowHeatMapMigrationOccupations(true);
        setMigrationSectorsHeatMap([]);
        setMigrationOccupationsHeatMap([]);
        setSectorMigrationHeatMapXAxisLabels([]);
        setSectorMigrationHeatMapYAxisLabels([]);
        setOccupationMigrationHeatMapXAxisLabels([]);
        setOccupationMigrationHeatMapYAxisLabels([]);
        setTopSkillGroup(WSSkillGroups.HardSkill);
        setISCO3Search("");
        setShowMostRequiredISCO4JobInfo(true);
        setTotalRanking(0);
        setCurrentRanking(0);
    }, [setISCO3OccupationList, setMostRequiredISCO4JobInfo, setMostRequiredJobInfo]);

    useEffect(() => {
        // Reset all ISCO3/ISCO4 related states after any change in mostRequiredJobInfo
        setMostRequiredISCO4JobInfo && setMostRequiredISCO4JobInfo(null);
        setISCO3OccupationList && setISCO3OccupationList([]);
        setISCO3OccupationListOrderByRanking && setISCO3OccupationListOrderByRanking([]);
        setFilteredISCO3OccupationListOrderByRanking && setFilteredISCO3OccupationListOrderByRanking([]);
        setPercentageJobPosts("0");
        setShowMostRequiredISCO4JobInfo(true);
        setTotalRanking(0);
        setCurrentRanking(0);
    }, [mostRequiredJobInfo, setISCO3OccupationList, setMostRequiredISCO4JobInfo, setMostRequiredJobInfo]);

    useEffect(() => {
        var fromSectors: any[] = [];
        var toSectors: any[] = [];
        var tempMemberMigrationSectors: types.MemberMigrationSectorsTypes[] = [];
        if (memberMigrationSectors && memberMigrationSectors.length > 0) {
            // Get unique from/to sectors
            fromSectors = [...new Set(memberMigrationSectors.map((e: types.MemberMigrationSectorsTypes) => e.fromISICLabel))];
            toSectors = [...new Set(memberMigrationSectors.map((e: types.MemberMigrationSectorsTypes) => e.toISICLabel))];

            // Loop through all migration and check which from sector doesn't have to sector and add it to tempMemberMigrationSectors with totalCount = 0 otherwise add it as it is from memberMigrationSectors
            fromSectors.forEach((fromSector: string) => {
                toSectors.forEach((toSector: string) => {
                    var existMigration = memberMigrationSectors.find((migration: types.MemberMigrationSectorsTypes) => migration.fromISICLabel === fromSector && migration.toISICLabel === toSector);
                    if (!existMigration) {
                        var fromSectorMigration = memberMigrationSectors.find((migration: types.MemberMigrationSectorsTypes) => migration.fromISICLabel === fromSector);
                        var toSectorMigration = memberMigrationSectors.find((migration: types.MemberMigrationSectorsTypes) => migration.toISICLabel === toSector);
                        if (fromSectorMigration && toSectorMigration) {
                            tempMemberMigrationSectors.push({ fromISICCode: fromSectorMigration?.fromISICCode, fromISICLabel: fromSectorMigration?.fromISICLabel, toISICCode: toSectorMigration.toISICCode, toISICLabel: toSectorMigration.toISICLabel, totalCount: 0 });
                        }
                    }
                    else {
                        tempMemberMigrationSectors.push(existMigration);
                    }
                });
            });
        }
        // Sort the final member migration sector array after filling the gaps
        const sortedTempMemberMigrationSectors = tempMemberMigrationSectors.sort((a: types.MemberMigrationSectorsTypes, b: types.MemberMigrationSectorsTypes) => b.totalCount - a.totalCount)
        setMigrationSectorsHeatMap(sortedTempMemberMigrationSectors);

        // Sort from sectors in a reverse way and get unique values to be shown on the yAxis
        const sortedFromSectors = [...new Set(sortedTempMemberMigrationSectors.map((e: types.MemberMigrationSectorsTypes) => e.fromISICLabel).reverse())];
        // get unique values to sectors to be shown on the xAxis
        const sortedToSectors = [...new Set(sortedTempMemberMigrationSectors.map((e: types.MemberMigrationSectorsTypes) => e.toISICLabel))];

        const xAxisData = sortedToSectors.map(toISICLabel => ({
            toISICLabel,
        }));

        const yAxisData = sortedFromSectors.map(fromISICLabel => ({
            fromISICLabel,
        }));

        setSectorMigrationHeatMapXAxisLabels(xAxisData);
        setSectorMigrationHeatMapYAxisLabels(yAxisData);
    }, [memberMigrationSectors]);


    useEffect(() => {
        var fromOccupations: any[] = [];
        var toOccupations: any[] = [];
        var tempMemberMigrationOccupations: types.MemberMigrationOccupationsTypes[] = [];
        if (memberMigrationOccupations && memberMigrationOccupations.length > 0) {
            // Get unique from/to occupations
            fromOccupations = [...new Set(memberMigrationOccupations.map((e: types.MemberMigrationOccupationsTypes) => e.fromISCO2Label))];
            toOccupations = [...new Set(memberMigrationOccupations.map((e: types.MemberMigrationOccupationsTypes) => e.toISCO2Label))];

            // Loop through all migration and check which from occupation doesn't have to occupation and add it to tempMemberMigrationOccupations with totalCount = 0 otherwise add it as it is from memberMigrationOccupations
            fromOccupations.forEach((fromOccupation: string) => {
                toOccupations.forEach((toOccupation: string) => {
                    var existMigration = memberMigrationOccupations.find((migration: types.MemberMigrationOccupationsTypes) => migration.fromISCO2Label === fromOccupation && migration.toISCO2Label === toOccupation);
                    if (!existMigration) {
                        var fromOccupationMigration = memberMigrationOccupations.find((migration: types.MemberMigrationOccupationsTypes) => migration.fromISCO2Label === fromOccupation);
                        var toOccupationMigration = memberMigrationOccupations.find((migration: types.MemberMigrationOccupationsTypes) => migration.toISCO2Label === toOccupation);
                        if (fromOccupationMigration && toOccupationMigration) {
                            tempMemberMigrationOccupations.push({ fromISCO2Code: fromOccupationMigration?.fromISCO2Code, fromISCO2Label: fromOccupationMigration?.fromISCO2Label, toISCO2Code: toOccupationMigration.toISCO2Code, toISCO2Label: toOccupationMigration.toISCO2Label, totalCount: 0 });
                        }
                    }
                    else {
                        tempMemberMigrationOccupations.push(existMigration);
                    }
                });
            });
        }
        // Sort the final member migration occupation array after filling the gaps
        const sortedTempMemberMigrationOccupations = tempMemberMigrationOccupations.sort((a: types.MemberMigrationOccupationsTypes, b: types.MemberMigrationOccupationsTypes) => b.totalCount - a.totalCount)
        setMigrationOccupationsHeatMap(sortedTempMemberMigrationOccupations);

        // Sort from occupations in a reverse way and get unique values to be shown on the yAxis
        const sortedFromOccupations = [...new Set(sortedTempMemberMigrationOccupations.map((e: types.MemberMigrationOccupationsTypes) => e.fromISCO2Label).reverse())];
        // get unique values to occupations to be shown on the xAxis
        const sortedToOccupations = [...new Set(sortedTempMemberMigrationOccupations.map((e: types.MemberMigrationOccupationsTypes) => e.toISCO2Label))];

        const xAxisData = sortedToOccupations.map(toISCO2Label => ({
            toISCO2Label,
        }));

        const yAxisData = sortedFromOccupations.map(fromISCO2Label => ({
            fromISCO2Label,
        }));

        setOccupationMigrationHeatMapXAxisLabels(xAxisData);
        setOccupationMigrationHeatMapYAxisLabels(yAxisData);
    }, [memberMigrationOccupations]);


    const topEmployersTableCols = [
        {
            key: 'companyName',
            label: 'Top Employer',
        },
        {
            key: 'numJobs',
            label: 'Number of job posts',
        },
    ]

    const topOccupationsBySectorsTableCols = [
        {
            key: 'occupationCategoryName',
            label: 'Occupation name',
        },
        {
            key: 'iscO2Code',
            label: 'ISCO2 code',
        },
        {
            key: 'jobCount',
            label: 'Number of job posts',
        },
        {
            key: 'percentage',
            label: 'Market share',
        },
    ]

    useEffect(() => {
        const x = yearlyOccupationTrend ? yearlyOccupationTrend : []
        let yearResult = 0
        switch (x.length) {
            case 0: yearResult = 0
                break;
            case 1: yearResult = 100
                break;
            case 2: yearResult = 100 * (x[x.length - 1].value - x[0].value) / x[0].value
                break;
            case 3: yearResult = 100 * (x[x.length - 1].value - x[0].value) / x[0].value
                break;
            case 4: yearResult = 100 * (x[x.length - 1].value - x[0].value) / x[0].value
                break;
            default: yearResult = 100 * (x[x.length - 1].value - x[x.length - 5].value) / x[x.length - 5].value
        }

        if (selectedProfile.occupation.length === 1 && selectedProfile.sector.length === 0) {
            setYearlyPercentSentence(`Demand for ${selectedProfile.occupation[0].title} has ${yearResult < 0 ? "declined" : "grown"} by ${Math.round(yearResult)}% year on year.`)
        } else if (selectedProfile.occupation.length > 1 && selectedProfile.sector.length === 0) {
            setYearlyPercentSentence(`Demand for selected occupations has ${yearResult < 0 ? "declined" : "grown"} by ${Math.round(yearResult)}% year on year.`)
        } else if (selectedProfile.sector.length === 1 && selectedProfile.occupation.length === 0) {
            setYearlyPercentSentence(`Demand in ${selectedProfile.sector[0].title} has ${yearResult < 0 ? "declined" : "grown"} by ${Math.round(yearResult)}% year on year.`)
        } else if (selectedProfile.sector.length > 1 && selectedProfile.occupation.length === 0) {
            setYearlyPercentSentence(`Demand in selected sectors has ${yearResult < 0 ? "declined" : "grown"} by ${Math.round(yearResult)}% year on year.`)
        } else if (selectedProfile.sector.length > 0 && selectedProfile.occupation.length > 0) {
            setYearlyPercentSentence(`Demand for selected occupation in selected sectors ${yearResult < 0 ? "declined" : "grown"} by ${Math.round(yearResult)}% year on year.`)
        } else {
            setYearlyPercentSentence("")
        }


    }, [yearlyOccupationTrend]);// eslint-disable-line




    const handleSelectedOccupationISCO4 = async (isco3Code: string, isco4Code?: string) => {
        if (!isco4Code) {
            var isco3 = filteredISCO3OccupationListOrderByRanking.find(isco3 => isco3.id === isco3Code);
            if (isco3 && isco3.children && isco3.children.length > 0) {
                isco4Code = isco3.children[0].id;
            }
        }

        if (isco4Code) {
            const occupationIds = selectedProfile.occupation.length ? selectedProfile.occupation.map(e => e.id) : [];
            const sectorIds = selectedProfile.sector.length ? selectedProfile.sector.map(e => e.id) : [];
            setIsOccupationLoading(true)
            const [errMostRequiredISCO4JobInfo, incomingMostRequiredISCO4JobInfo] = await getMostRequiredISCO4JobInfo([countryData.hubId], sectorIds, occupationIds, [isco4Code], (selectedProfile.provinces && selectedProfile.provinces.length) ? selectedProfile.provinces.map((e: any) => e.title) : [], selectedProfile.fromTimeline?.title, selectedProfile.toTimeline?.title, selectedProfile.isEntryLevel);
            if (errMostRequiredISCO4JobInfo) {
                console.warn(errMostRequiredISCO4JobInfo);
            } else {
                setMostRequiredISCO4JobInfo && setMostRequiredISCO4JobInfo(incomingMostRequiredISCO4JobInfo);
                setOccupationISCO3selected(isco3Code);
                setOccupationISCO4selected(isco4Code);
            }
            const [getISCO4OccupationAisErr, getISCO4OccupationAisRes] = await url.getISCO4OccupationAis([isco4Code]);
            if (getISCO4OccupationAisErr) {
                console.warn(getISCO4OccupationAisErr);
            } else {
                setISCO4OccupationAiList && setISCO4OccupationAiList(getISCO4OccupationAisRes);
            }

            setIsOccupationLoading(false)
        }

        if (filteredISCO3OccupationListOrderByRanking && filteredISCO3OccupationListOrderByRanking.length > 0) {
            let selectedIsco4lIST = filteredISCO3OccupationListOrderByRanking.map(obj =>
                obj.children?.map((isco4: types.SelectedProfileChildrenTypes) => {
                    return isco4.id;
                }));
            if (selectedIsco4lIST && selectedIsco4lIST.length > 0) {
                const selectedIsco4: string[] = selectedIsco4lIST.flat().filter(item => item !== null) as string[];
                setAiChartIsco4SelectedList && setAiChartIsco4SelectedList(selectedIsco4);
            }
        }
    }

    const setAIIsco4Data = async (ISCO3OccupationListOrderByRankingList: types.SelectedProfileChildrenTypes[]) => {

        if (ISCO3OccupationListOrderByRankingList && ISCO3OccupationListOrderByRankingList.length > 0) {
            let selectedIsco4lIST = ISCO3OccupationListOrderByRankingList.map(obj =>
                obj.children?.map((isco4: types.SelectedProfileChildrenTypes) => {
                    return isco4.id;
                }));
            if (selectedIsco4lIST && selectedIsco4lIST.length > 0) {

                const selectedIsco4: string[] = selectedIsco4lIST.flat().filter(item => item !== null) as string[];
                setAiChartIsco4SelectedList && setAiChartIsco4SelectedList(selectedIsco4);
            }
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                setIsLoadingIsco4AIData(false);
                const [getIsco4AIChartDataErr, getIsco4AIChartDataRes] = await url.getIsco4AIChartData(aiChartIsco4SelectedList);
                if (getIsco4AIChartDataErr) {
                    console.warn(getIsco4AIChartDataErr);
                } else {
                    setIsco4AIChartDataList && setIsco4AIChartDataList(getIsco4AIChartDataRes);
                }
                setIsLoadingIsco4AIData(true);
            } catch (error) {
                console.error('Error fetching data:', error);
            }
        };

        fetchData();
    }, [aiChartIsco4SelectedList]);

    const exportJobPosts = async () => {
        setIsLoadingExportJobPosts(true);
        const sectorIds = selectedProfile.sector.length ? selectedProfile.sector.map(e => e.id) : [];
        const [err, res] = await getJobPostsInfo([countryData.hubId], sectorIds, [occupationISCO4selected], (selectedProfile.provinces && selectedProfile.provinces.length) ? selectedProfile.provinces.map((e: any) => e.title) : [], selectedProfile.fromTimeline?.title, selectedProfile.toTimeline?.title, selectedProfile.isEntryLevel);
        if (err) console.warn(err);
        if (res) {
            const url = window.URL.createObjectURL(new Blob([res]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", "jobPosts-" + countryData.country.replaceAll(" ", "") + ".xlsx");
            document.body.appendChild(link);
            link.click();
        }
        setIsLoadingExportJobPosts(false);
    }

    const exportISCO4AsPDF = async () => {
        setIsLoadingExportISCO4(true);
        const sectorIds = selectedProfile.sector.length ? selectedProfile.sector.map(e => e.id) : [];
        const occupationIds = selectedProfile.occupation.length ? selectedProfile.occupation.map(e => e.id) : [];
        const [errExportedISCO4, incomingExportedISCO4] = await exportISCO4([countryData.hubId], sectorIds, occupationIds, [occupationISCO4selected], (selectedProfile.provinces && selectedProfile.provinces.length) ? selectedProfile.provinces.map((e: any) => e.title) : [], selectedProfile.fromTimeline?.title, selectedProfile.toTimeline?.title, currentRanking, totalRanking, selectedProfile.isEntryLevel);
        if (errExportedISCO4) console.warn(errExportedISCO4);
        if (incomingExportedISCO4) {
            const url = window.URL.createObjectURL(new Blob([incomingExportedISCO4]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", "isco4-" + mostRequiredISCO4JobInfo?.isco4_label?.replace(/(^\w|\s\w)/g, m => m.toUpperCase()).replaceAll(" ", "") + "-" + countryData.country.replaceAll(" ", "") + ".pdf");
            document.body.appendChild(link);
            link.click();
        }
        setIsLoadingExportISCO4(false);
    }

    useEffect(() => {
        // Reset ISCO3 Search
        setISCO3Search("");

        if (ISCO3OccupationList && ISCO3OccupationList.length) {
            // Order ISCO3OccupationList by ranking
            if (mostRequiredJobInfo && mostRequiredJobInfo.isco3_job_counts && mostRequiredJobInfo.isco3_job_counts.length > 0) {
                var tempISCO3OccupationListOrderByRanking: types.SelectedProfileChildrenTypes[] = [];
                mostRequiredJobInfo.isco3_job_counts.forEach(isco3_job_count => {
                    var isco3 = ISCO3OccupationList.find(i => i.id === isco3_job_count.isco3_code);
                    if (isco3 && isco3.children && isco3.children.length > 0) {
                        // Order ISCO4OccupationList by ranking
                        var tempISCO4OccupationListOrderByRanking: types.SelectedProfileChildrenTypes[] = [];
                        if (isco3_job_count.isco4 && isco3_job_count.isco4.length > 0) {
                            isco3_job_count.isco4.forEach((isco4: any) => {
                                if (isco3 && isco3.children && isco3.children.length > 0) {
                                    var tempISCO4: types.SelectedProfileChildrenTypes = isco3.children.find((currentISCO4: types.SelectedProfileChildrenTypes) => currentISCO4.id === isco4.isco4_code);
                                    if (tempISCO4) {
                                        tempISCO4OccupationListOrderByRanking.push(tempISCO4);
                                    }
                                }
                            });

                            // Add to last the unmatched isco3 occupations
                            var unmatchedISCO4OccupationListOrderByRanking: types.SelectedProfileChildrenTypes[] = [];
                            isco3.children.forEach((isco4_occupation: types.SelectedProfileChildrenTypes) => {
                                if (tempISCO4OccupationListOrderByRanking.findIndex(i => i.id === isco4_occupation.id) === -1)
                                    unmatchedISCO4OccupationListOrderByRanking.push(isco4_occupation);
                            });
                            if (unmatchedISCO4OccupationListOrderByRanking && unmatchedISCO4OccupationListOrderByRanking.length > 0)
                                tempISCO4OccupationListOrderByRanking = tempISCO4OccupationListOrderByRanking.concat(unmatchedISCO4OccupationListOrderByRanking);

                            isco3.children = tempISCO4OccupationListOrderByRanking;
                        }
                        tempISCO3OccupationListOrderByRanking.push(isco3);
                    }
                });

                // Add to last the unmatched isco3 occupations
                var unmatchedISCO3OccupationListOrderByRanking: types.SelectedProfileChildrenTypes[] = [];
                ISCO3OccupationList.forEach(isco3_occupation => {
                    if (tempISCO3OccupationListOrderByRanking.findIndex(i => i.id === isco3_occupation.id) === -1)
                        unmatchedISCO3OccupationListOrderByRanking.push(isco3_occupation);
                });
                if (unmatchedISCO3OccupationListOrderByRanking && unmatchedISCO3OccupationListOrderByRanking.length > 0)
                    tempISCO3OccupationListOrderByRanking = tempISCO3OccupationListOrderByRanking.concat(unmatchedISCO3OccupationListOrderByRanking);

                // Set ISCO3 Occupation List Ordered By Ranking
                setISCO3OccupationListOrderByRanking(tempISCO3OccupationListOrderByRanking);
                setFilteredISCO3OccupationListOrderByRanking(tempISCO3OccupationListOrderByRanking);
                setAIIsco4Data(tempISCO3OccupationListOrderByRanking);
                if (tempISCO3OccupationListOrderByRanking[0].id && tempISCO3OccupationListOrderByRanking[0].children && tempISCO3OccupationListOrderByRanking[0].children.length > 0) {
                    handleSelectedOccupationISCO4(tempISCO3OccupationListOrderByRanking[0].id, tempISCO3OccupationListOrderByRanking[0].children[0].id);
                }
            }
            else {
                setISCO3OccupationListOrderByRanking(ISCO3OccupationList);
                setFilteredISCO3OccupationListOrderByRanking(ISCO3OccupationList);
                setAIIsco4Data(ISCO3OccupationList);
                if (ISCO3OccupationList[0].id && ISCO3OccupationList[0].children && ISCO3OccupationList[0].children.length > 0) {
                    handleSelectedOccupationISCO4(ISCO3OccupationList[0].id, ISCO3OccupationList[0].children[0].id);
                }
            }
        }
        else {
            setOccupationISCO3selected("");
            setOccupationISCO4selected("");
            setTotalRanking(0);
        }
    }, [ISCO3OccupationList]);// eslint-disable-line

    useEffect(() => {
        // Calculate percentage of selected isco4
        if (mostRequiredJobInfo && mostRequiredISCO4JobInfo) {
            var percentage = mostRequiredJobInfo.total_jobs > 0 && mostRequiredISCO4JobInfo.total_jobs > 0 ? (mostRequiredISCO4JobInfo.total_jobs / mostRequiredJobInfo.total_jobs * 100).toFixed(1) : "0";
            setPercentageJobPosts(percentage);
        }
        else {
            setPercentageJobPosts("100");
        }

        // Set Total Ranking
        if (ISCO3OccupationList) {
            var tempTotalRanking = ISCO3OccupationList.reduce((totalRank, isco3) => totalRank + (isco3.children ? isco3.children.length : 0), 0);
            setTotalRanking(tempTotalRanking);
        }
        else {
            setTotalRanking(0);
        }
    }, [mostRequiredJobInfo, mostRequiredISCO4JobInfo, ISCO3OccupationList]);

    useEffect(() => {
        // Search title, description, tasks for both ISCO3 and ISCO4 levels
        let filteredISCO3: types.SelectedProfileChildrenTypes[] = [];
        if (isco3Search) {
            filteredISCO3 = isco3OccupationListOrderByRanking.filter((value: types.SelectedProfileChildrenTypes) => {
                return (value.title && value.title.includes(isco3Search)) ||
                    (value.description && value.description.includes(isco3Search)) ||
                    (value.tasks && value.tasks.includes(isco3Search)) ||
                    (value.children && value.children.length > 0 && value.children.some((valueChildren: types.SelectedProfileChildrenTypes) => (valueChildren.title && valueChildren.title.includes(isco3Search)) || (valueChildren.description && valueChildren.description.includes(isco3Search)) || (valueChildren.tasks && valueChildren.tasks.includes(isco3Search))));
            });
            setFilteredISCO3OccupationListOrderByRanking(filteredISCO3);


        }
        else {
            filteredISCO3 = isco3OccupationListOrderByRanking;
            setFilteredISCO3OccupationListOrderByRanking(filteredISCO3);
        }
    }, [isco3Search, isco3OccupationListOrderByRanking, mostRequiredJobInfo, mostRequiredISCO4JobInfo, occupationISCO4selected]);


    // Set Current Ranking
    useEffect(() => {
        if (occupationISCO3selected && occupationISCO4selected && isco3OccupationListOrderByRanking) {
            var tempCurrentTanking = 0;
            for (const isco3 of isco3OccupationListOrderByRanking) {
                if (isco3.id !== occupationISCO3selected) {
                    tempCurrentTanking += (isco3.children ? isco3.children.length : 0);
                }
                else {
                    if (isco3.children && isco3.children.length > 0) {
                        var isco4Index = isco3.children.findIndex((isco4: types.SelectedProfileChildrenTypes) => isco4.id === occupationISCO4selected) + 1;
                        tempCurrentTanking += isco4Index;
                    }
                    break;
                }
            }
            setCurrentRanking(tempCurrentTanking);
        }
        else {
            setCurrentRanking(0);
        }
    }, [occupationISCO3selected, occupationISCO4selected, isco3OccupationListOrderByRanking]);

    const isco4Ids = useMemo(() => {
        var isco4s = [];
        for (const isco3 of isco3OccupationListOrderByRanking) {
            if (isco3 && isco3.children && isco3.children.length > 0) {
                for (const isco4 of isco3.children) {
                    isco4s.push(isco4.id);
                }
            }
        }
        return isco4s;
    }, [isco3OccupationListOrderByRanking]);

    const handleChangeSkillType = useCallback(() => {
        setSkillType((prevSkillsType: string) => (prevSkillsType === SkillTypes.WS ? SkillTypes.ESCO : SkillTypes.WS));
    }, []);

    return (
        <div className="occupation-tab">
            {(selectedProfile.occupation.length || selectedProfile.sector.length) ?
                <>
                    {isLoading > 0 ? <>
                        <div className="mb-3">
                            <div className="widget-component">
                                <div className="total-job-post-number-container">
                                    <div className="total-job-post-number">
                                        <div className="d-flex total-job-post align-items-center">
                                            <h1>{totalJobPosts?.total_job_posts_count}</h1>
                                            <h3>Total job posts for {titles} for the selected period</h3>
                                        </div>
                                    </div>
                                    <div className="total-job-post-number historic-numbers">
                                        <div className="d-flex total-job-post align-items-center">
                                            <h1>{totalJobPosts?.historic_total_job_posts_count}</h1>
                                            <h3>Total historic job posts for {titles}</h3>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="row mb-3">
                            <div className="col-4">
                                <div className="widget-component">
                                    <h4 className="border-bottom mb-2 pb-2">
                                        Top employers for {selectedProfile.occupation.length
                                            ?
                                            ((selectedProfile.occupation.length === 1 ? selectedProfile.occupation[0].title : "selected occupation(s)") + (selectedProfile.sector.length > 0 ? " in selected sector(s)" : ""))
                                            :
                                            (selectedProfile.sector.length === 1 ? selectedProfile.sector[0].title : "selected sector(s)")
                                        }
                                    </h4>
                                    {topNEmployers && topNEmployers.length ?
                                        <div>
                                            <ExportButton countryName={countryData.country} exportDOMId="topEmployers" urlAPI={url.exportTopNEmployers} urlDataAPI={{
                                                hubIds: [countryData.hubId],
                                                sectorIds: selectedProfile.sector.length ? selectedProfile.sector.map((e: any) => e.id) : [],
                                                occupationCategoryIds: selectedProfile.occupation.length ? selectedProfile.occupation.map((e: any) => e.id) : [],
                                                locations: (selectedProfile.provinces && selectedProfile.provinces.length) ? selectedProfile.provinces.map((e: any) => e.title) : [],
                                                fromTimeline: selectedProfile.fromTimeline?.title,
                                                toTimeline: selectedProfile.toTimeline?.title,
                                                isEntryLevel: selectedProfile.isEntryLevel
                                            }} />
                                            <Table id="topEmployers" columns={topEmployersTableCols} data={topNEmployers} />
                                        </div>
                                        :
                                        <div className="na">N/A</div>
                                    }
                                </div>
                            </div>

                            <div className="col-8">
                                <div className="widget-component historic-numbers">
                                    <h4 className="border-bottom mb-2 pb-2">Historic job posts in {titles}</h4>
                                    {yearlyOccupationTrend && yearlyOccupationTrend.length ?
                                        <div>
                                            <ExportButton countryName={countryData.country} exportDOMId="historicalJobs-chart-container" urlAPI={url.exportYearlyOccupationTrend} urlDataAPI={{
                                                hubIds: [countryData.hubId],
                                                sectorIds: selectedProfile.sector.length ? selectedProfile.sector.map((e: any) => e.id) : [],
                                                occupationCategoryIds: selectedProfile.occupation.length ? selectedProfile.occupation.map((e: any) => e.id) : [],
                                                locations: selectedProfile.provinces.length ? selectedProfile.provinces.map((e: any) => e.title) : [],
                                                year: null,
                                                yearlyPercentSentence: yearlyPercentSentence,
                                                isEntryLevel: selectedProfile.isEntryLevel
                                            }} />
                                            <div id="historicalJobs-chart-container">
                                                <LineChart name="historicalJobs" data={yearlyOccupationTrend} xLabels={{ rotation: -20 }} titleY="Number of job posts" selectedQuarter={{ from: selectedProfile.fromTimeline?.title, to: selectedProfile.toTimeline?.title }} />
                                                <p className="text-center">{yearlyPercentSentence}</p>
                                            </div>
                                        </div>
                                        :
                                        <div className="na">N/A</div>
                                    }
                                </div>
                            </div>


                        </div></> : null}

                    {topOccupationsBySectors && topOccupationsBySectors.length ? <>
                        {isLoading > 1 ? <div className="mb-3">
                            <div className="widget-component">
                                <h4 className="border-bottom mb-2 pb-2">Top Occupations</h4>
                                <div>
                                    <ExportButton countryName={countryData.country} exportDOMId="topOccupations" urlAPI={url.exportTopOccupationsBySectors} urlDataAPI={{
                                        hubIds: [countryData.hubId],
                                        sectorIds: selectedProfile.sector.length ? selectedProfile.sector.map((e: any) => e.id) : [],
                                        locations: (selectedProfile.provinces && selectedProfile.provinces.length) ? selectedProfile.provinces.map((e: any) => e.title) : [],
                                        fromTimeline: selectedProfile.fromTimeline?.title,
                                        toTimeline: selectedProfile.toTimeline?.title,
                                        isEntryLevel: selectedProfile.isEntryLevel
                                    }} />
                                    <Table id="topOccupations" columns={topOccupationsBySectorsTableCols} data={topOccupationsBySectors.map(e => {
                                        e.percentage = typeof (e.percentage) === "number" ? e.percentage + "%" : e.percentage;
                                        return e;
                                    })} />
                                </div>
                            </div>
                        </div>
                            :
                            null}
                    </> : null}

                    {isLoading > 2 ? <><div className="mb-3">

                        <div className="widget-component">
                            <h4 className="border-bottom mb-2 pb-2">Requirements of {titles} </h4>
                            {(mostRequiredJobInfo || selectedProfile.occupation.length || mostRequiredISCO4JobInfo) ?
                                <div id="mostRequiredJobInfo" className="occupation-tab__specialization">
                                    <ExportButton className={(!showMostRequiredISCO4JobInfo ? " d-none " : "")} countryName={countryData.country} exportDOMId="mostRequiredJobInfo" urlAPI={url.exportMostRequiredJobInfo} urlDataAPI={{
                                        mostRequiredJobsTitle: "Requirements of " + titles,
                                        hubIds: [countryData.hubId],
                                        sectorIds: selectedProfile.sector.length ? selectedProfile.sector.map((e: any) => e.id) : [],
                                        occupationCategoryIds: selectedProfile.occupation.length ? selectedProfile.occupation.map((e: any) => e.id) : [],
                                        Isco4Ids: isco4Ids,
                                        locations: (selectedProfile.provinces && selectedProfile.provinces.length) ? selectedProfile.provinces.map((e: any) => e.title) : [],
                                        fromTimeline: selectedProfile.fromTimeline?.title,
                                        toTimeline: selectedProfile.toTimeline?.title,
                                        isEntryLevel: selectedProfile.isEntryLevel
                                    }} />
                                    <div className="row">
                                        <div className="col-4">
                                            {(mostRequiredJobInfo) ? <>
                                                {mostRequiredJobInfo.specializations && mostRequiredJobInfo.specializations.length ? <div className="mb-3">
                                                    <h4>Most required fields of education: </h4>
                                                    {mostRequiredJobInfo.specializations.map((e: string, i: number) => <div key={i}><ArrowRight /> {e}​</div>)}
                                                </div> : null}
                                                {mostRequiredJobInfo.education_level ? <div className="mb-3">
                                                    <h4>Most required education level: </h4>
                                                    <ArrowRight /> {mostRequiredJobInfo.education_level}
                                                </div> : null}
                                                {mostRequiredJobInfo.experience_range ? <div className="mb-3">
                                                    <h4>Years of experience: </h4>
                                                    <ArrowRight /> {mostRequiredJobInfo.experience_range} years
                                                </div> : null}
                                            </> : <h6>NA</h6>}
                                        </div>
                                        <div className="col-3">
                                            {isco3OccupationListOrderByRanking && isco3OccupationListOrderByRanking.length ? <>
                                                <h4 className="mb-2">Explore occupations in detail​</h4>
                                                <TextField
                                                    id="isco3_search"
                                                    label="Search Occupations"
                                                    type="search"
                                                    variant="outlined"
                                                    size="small"
                                                    fullWidth
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setISCO3Search(e.target.value)}
                                                />
                                                <div >
                                                    <div className="occupation-tab__specialization--occupations-list">
                                                        {filteredISCO3OccupationListOrderByRanking?.map((isco3: types.SelectedProfileChildrenTypes) => {
                                                            return <div key={isco3.id}>
                                                                <div key={isco3.id} className={"btnISCO3Occupation " + (occupationISCO3selected === isco3.id ? "selected" : "")} onClick={() => handleSelectedOccupationISCO4(isco3.id)}>
                                                                    <div className="ISCO3OccupationTitle">{isco3.title}</div>
                                                                    {occupationISCO3selected === isco3.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                                </div>
                                                                {isco3.children && isco3.children.length > 0 ?
                                                                    <ul key={"child-container-" + isco3.id} className={"list-isco4-occupation " + (occupationISCO3selected !== isco3.id ? "hidden" : "")}>
                                                                        {isco3.children?.map((isco4: types.SelectedProfileChildrenTypes) => {
                                                                            return <li key={"child-" + isco4.id} className={"isco4-occupation " + (occupationISCO4selected === isco4.id ? "selected" : "")} onClick={() => handleSelectedOccupationISCO4(isco3.id, isco4.id)} >
                                                                                {isco4.title}
                                                                            </li>
                                                                        })}
                                                                    </ul>
                                                                    :
                                                                    <></>
                                                                }
                                                            </div>
                                                        })}
                                                    </div>
                                                </div>
                                            </> : null}
                                        </div>
                                        <div className={"col-5 position-relative" + (!showMostRequiredISCO4JobInfo ? " d-none " : "")}>
                                            {isOccupationLoading === true ? <img src={LoaderImage} alt="" className="loadingImg" style={{ width: 20 }} /> : mostRequiredISCO4JobInfo ? <>
                                                {mostRequiredISCO4JobInfo.isco4_label ?
                                                    <>
                                                        <div className="col-md-12">
                                                            {isco3OccupationListOrderByRanking.length > 0 && isco3OccupationListOrderByRanking.findIndex(i => i.id === occupationISCO3selected) > -1 &&
                                                                <span>Ranking #{currentRanking} of {totalRanking} occupations</span>
                                                            }
                                                            {isLoadingExportISCO4 ?
                                                                <img src={LoaderImage} alt="" className="loadingImg loadingImgJobPosts float-end" style={{ width: 20 }} />
                                                                :
                                                                <ArrowCircleDown type="button" className="float-end" onClick={exportISCO4AsPDF} />
                                                            }
                                                        </div>
                                                        <div className="col-md-12">
                                                            <h3 className="d-inline-block mb-3 isco3-title">{mostRequiredISCO4JobInfo.isco4_label}</h3>
                                                            <img src={Leaf} alt="" className="leaf" style={{ opacity: mostRequiredISCO4JobInfo.green_score ? mostRequiredISCO4JobInfo.green_score : 0 }} />
                                                        </div>
                                                    </>
                                                    : <></>}
                                                {mostRequiredISCO4JobInfo.specializations && mostRequiredISCO4JobInfo.specializations.length ? <div className="mb-3">
                                                    <h4>Fields of education: </h4>
                                                    {mostRequiredISCO4JobInfo.specializations.map((e: string, i: number) => <div key={i}><ArrowRight /> {e}​</div>)}
                                                </div> : <></>}
                                                {(!mostRequiredISCO4JobInfo.total_jobs || mostRequiredISCO4JobInfo.total_jobs === 0) ? <></> : <div className="row mb-3">
                                                    {mostRequiredISCO4JobInfo.education_levels && mostRequiredISCO4JobInfo.education_levels.length > 0 ? <div className="col-md-6">
                                                        <h4>Education level: </h4>
                                                        <ArrowRight /> {mostRequiredISCO4JobInfo.education_levels[0].education_level_name}
                                                    </div> : <></>}
                                                    {mostRequiredISCO4JobInfo.experience_range ? <div className="col-md-6">
                                                        <h4>Years of experience: </h4>
                                                        <ArrowRight /> {mostRequiredISCO4JobInfo.experience_range} years
                                                    </div> : <></>}
                                                </div>}
                                                <div className="row mb-3">
                                                    {(!mostRequiredISCO4JobInfo.total_jobs || mostRequiredISCO4JobInfo.total_jobs === 0) ? <div className="col-md-6 text-center">
                                                        <h6>No job posts available</h6>
                                                    </div> : <div className="col-md-6">
                                                        <h4>Number of job posts and share of ISCO 2-digit group: </h4>
                                                        <ArrowRight />
                                                        <>
                                                            {isLoadingExportJobPosts ?
                                                                <img src={LoaderImage} alt="" className="loadingImg loadingImgJobPosts" style={{ width: 20 }} />
                                                                :
                                                                <span role="button" onClick={exportJobPosts}>{mostRequiredISCO4JobInfo.total_jobs.toLocaleString()}</span>
                                                            }
                                                            <span className="totalJobPosts">{mostRequiredJobInfo?.total_jobs.toLocaleString()}</span>
                                                        </>
                                                        <div className="percentageContainer">
                                                            <div className="percentageBar">
                                                                <div className="percentageBarContent" style={{ width: percentageJobPosts + "%" }}>
                                                                    <div className="percentageBarContentValue">{percentageJobPosts}%</div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>}
                                                    {mostRequiredISCO4JobInfo.isco4_code ? <div className="col-md-6">
                                                        <h4>ISCO 4-digit code: </h4>
                                                        <ArrowRight /> {mostRequiredISCO4JobInfo.isco4_code}
                                                    </div> : <></>}
                                                </div>
                                                {mostRequiredISCO4JobInfo.skills && mostRequiredISCO4JobInfo.skills.length ? <div className="mb-3">
                                                    <h4>Skills: </h4>
                                                    <ul className="occupation-tab__specialization--skills-list listCont">
                                                        {mostRequiredISCO4JobInfo.skills.map((e: string, i: number) => <li key={i}>{e}​</li>)}
                                                    </ul>
                                                </div> : <></>}
                                                {mostRequiredISCO4JobInfo && mostRequiredISCO4JobInfo.summary_monthly_salaries && mostRequiredISCO4JobInfo.summary_monthly_salaries.minimum_salary && mostRequiredISCO4JobInfo.summary_monthly_salaries.average_salary && mostRequiredISCO4JobInfo.summary_monthly_salaries.maximum_salary ?
                                                    <div className="mb-3">
                                                        <h4>Monthly Salaries: </h4>
                                                        <div className="monthly_salaries_container">
                                                            <div className="monthly_salary_experience_container">
                                                                <Tooltip title="Internship and Entry Level" placement="top" id="isco4_salary_tooltip_mid">
                                                                    <InfoIcon />
                                                                </Tooltip>
                                                                <div>Low Experience</div>
                                                                <div>{"$" + mostRequiredISCO4JobInfo.summary_monthly_salaries.minimum_salary.toLocaleString()}</div>
                                                            </div>
                                                            <div className="monthly_salary_experience_container">
                                                                <Tooltip title="Associate and Mid-Senior Level" placement="top" id="isco4_salary_tooltip_high">
                                                                    <InfoIcon />
                                                                </Tooltip>
                                                                <div>Mid Experience</div>
                                                                <div>{"$" + mostRequiredISCO4JobInfo.summary_monthly_salaries.average_salary.toLocaleString()}</div>
                                                            </div>
                                                            <div className="monthly_salary_experience_container">
                                                                <Tooltip title="Director and Executive" placement="top" id="isco4_salary_tooltip_high">
                                                                    <InfoIcon />
                                                                </Tooltip>
                                                                <div>High Experience</div>
                                                                <div>{"$" + mostRequiredISCO4JobInfo.summary_monthly_salaries.maximum_salary.toLocaleString()}</div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                : <></>}
                                                {mostRequiredISCO4JobInfo && mostRequiredISCO4JobInfo.job_demand_ai_impact_label &&
                                                    <div className="fields-job-demand-ai-impact-container">
                                                        <div className="fields-job-demand-ai-impact-title-container">
                                                            <span className="fields-job-demand-ai-impact-title">{mostRequiredISCO4JobInfo.job_demand_ai_impact_label} Job</span>
                                                            <span className={"fields-job-demand-ai-impact-title-circle " + mostRequiredISCO4JobInfo.job_demand_ai_impact_label}></span>
                                                        </div>
                                                        {mostRequiredISCO4JobInfo.creation_impact_reasoning &&
                                                            <Accordion className="fields-job-demand-ai-impact-category-title">
                                                                <AccordionSummary
                                                                    expandIcon={<ExpandMoreIcon className="icon" />}
                                                                    aria-controls="panel1a-content"
                                                                    id="panel1a-header"
                                                                >
                                                                    <Typography>Creation Impact</Typography>
                                                                </AccordionSummary>
                                                                <AccordionDetails>
                                                                    <Typography>
                                                                        {mostRequiredISCO4JobInfo.creation_impact_reasoning}
                                                                    </Typography>
                                                                </AccordionDetails>
                                                            </Accordion>
                                                        }
                                                        {mostRequiredISCO4JobInfo.productivity_impact_reasoning &&
                                                            <Accordion className="fields-job-demand-ai-impact-category-title">
                                                                <AccordionSummary
                                                                    expandIcon={<ExpandMoreIcon className="icon" />}
                                                                    aria-controls="panel1a-content"
                                                                    id="panel1a-header"
                                                                >
                                                                    <Typography>Productivity Impact</Typography>
                                                                </AccordionSummary>
                                                                <AccordionDetails>
                                                                    <Typography>
                                                                        {mostRequiredISCO4JobInfo.productivity_impact_reasoning}
                                                                    </Typography>
                                                                </AccordionDetails>
                                                            </Accordion>
                                                        }
                                                        {mostRequiredISCO4JobInfo.security_impact_reasoning &&
                                                            <Accordion className="fields-job-demand-ai-impact-category-title">
                                                                <AccordionSummary
                                                                    expandIcon={<ExpandMoreIcon className="icon" />}
                                                                    aria-controls="panel1a-content"
                                                                    id="panel1a-header"
                                                                >
                                                                    <Typography>Security Impact</Typography>
                                                                </AccordionSummary>
                                                                <AccordionDetails>
                                                                    <Typography>
                                                                        {mostRequiredISCO4JobInfo.security_impact_reasoning}
                                                                    </Typography>
                                                                </AccordionDetails>
                                                            </Accordion>
                                                        }
                                                        {mostRequiredISCO4JobInfo.wage_impact_reasoning &&
                                                            <Accordion className="fields-job-demand-ai-impact-category-title">
                                                                <AccordionSummary
                                                                    expandIcon={<ExpandMoreIcon className="icon" />}
                                                                    aria-controls="panel1a-content"
                                                                    id="panel1a-header"
                                                                >
                                                                    <Typography>Wage Impact</Typography>
                                                                </AccordionSummary>
                                                                <AccordionDetails>
                                                                    <Typography>
                                                                        {mostRequiredISCO4JobInfo.wage_impact_reasoning}
                                                                    </Typography>
                                                                </AccordionDetails>
                                                            </Accordion>
                                                        }
                                                    </div>
                                                }
                                            </> : <h6>NA</h6>}
                                        </div>
                                    </div>
                                </div>
                                :
                                <div className="na">N/A</div>
                            }
                        </div>
                    </div>

                        <div className="mb-3">

                            <div className="widget-component">
                                <h4 className="border-bottom mb-2 pb-2">Top Occupation Impacted By AI</h4>
                                {isLoadingIsco4AIData ?
                                    <> {isco4AIChartDataList && isco4AIChartDataList.length > 0 ?
                                        <div>
                                            <ExportButton countryName={countryData.country} exportDOMId="isco4ai-chart" urlAPI={url.exportIsco4AIChartData} urlDataAPI={{
                                                isco4Codes: aiChartIsco4SelectedList,
                                                hubIds: [countryData.hubId],
                                            }} />
                                            <div id="isco4ai-chart">
                                                <ColumnChart name="AIImpact" data={isco4AIChartDataList.slice(0, 5)} titleX="Isco4 Code" titleY="AI Impact Average" />
                                            </div>
                                        </div>
                                        : <div className="na">N/A</div>
                                    }
                                    </>
                                    : <div><img src={LoaderImage} alt="" className="mx-auto d-block" style={{ width: 30 }} /></div>
                                }
                            </div>
                        </div>

                    </>
                        : null}
                </> : <div>
                    <div className="mb-3">
                        <div className="widget-component">
                            <h4 className="border-bottom mb-2 pb-2">Top sectors by job demand</h4>
                            {sectorAndOccupationInfo && sectorAndOccupationInfo.sectors != null && sectorAndOccupationInfo.sectors.length > 0 ?
                                <div>
                                    <ExportButton countryName={countryData.country} exportDOMId="sectors-chart" urlAPI={url.exportTopSectorAndOccupationInfo} urlDataAPI={{
                                        hubIds: [countryData.hubId],
                                        locations: (selectedProfile.provinces && selectedProfile.provinces.length) ? selectedProfile.provinces.map((e: any) => e.title) : [],
                                        fromTimeline: selectedProfile.fromTimeline?.title,
                                        toTimeline: selectedProfile.toTimeline?.title,
                                        type: "Sector",
                                        isEntryLevel: selectedProfile.isEntryLevel
                                    }} />
                                    <div id="sectors-chart">
                                        <ColumnChart name="sectors" data={sectorAndOccupationInfo.sectors} titleX="Top sectors " titleY="Number of job posts" />
                                    </div>
                                </div>
                                :
                                <div className="na">N/A</div>
                            }
                        </div>
                    </div>
                    <div className="mb-3">
                        <div className="widget-component">
                            <h4 className="border-bottom mb-2 pb-2">Top occupations by job demand</h4>
                            {sectorAndOccupationInfo && sectorAndOccupationInfo.occupations != null && sectorAndOccupationInfo.occupations.length > 0 ?
                                <div>
                                    <ExportButton countryName={countryData.country} exportDOMId="occupations-chart" urlAPI={url.exportTopSectorAndOccupationInfo} urlDataAPI={{
                                        hubIds: [countryData.hubId],
                                        locations: (selectedProfile.provinces && selectedProfile.provinces.length) ? selectedProfile.provinces.map((e: any) => e.title) : [],
                                        fromTimeline: selectedProfile.fromTimeline?.title,
                                        toTimeline: selectedProfile.toTimeline?.title,
                                        type: "Occupation",
                                        isEntryLevel: selectedProfile.isEntryLevel
                                    }} />
                                    <div id="occupations-chart">
                                        <ColumnChart name="occupations" data={sectorAndOccupationInfo.occupations} titleX="Top occupations" titleY="Number of job posts" />
                                    </div>
                                </div>
                                :
                                <div className="na">N/A</div>
                            }
                        </div>
                    </div>


                </div>}

            {isLoading > 3 ? <div className="mb-3">
                <div className="widget-component">
                    <h4 className="border-bottom mb-2 pb-2">
                        Migration Sectors
                        {showHeatMapMigrationSectors
                            ?
                            <RepeatIcon className="bar-toggle-icon" onClick={() => setShowHeatMapMigrationSectors(false)} />
                            :
                            <AppsIcon className="bar-toggle-icon" onClick={() => setShowHeatMapMigrationSectors(true)} />
                        }
                    </h4>
                    {!showHeatMapMigrationSectors ?
                        (memberMigrationSectors && memberMigrationSectors.length) ?
                            <div>
                                <ExportButton countryName={countryData.country} exportDOMId="memberMigrationSectors-diagram" urlAPI={url.exportMemberMigrationSectors} urlDataAPI={{
                                    hubIds: [countryData.hubId]
                                }} />
                                <div id="memberMigrationSectors-diagram">
                                    <TraceableSankyDiagram name="memberMigrationSectors" data={memberMigrationSectors.slice(0, 10)} fromField="fromISICLabel" toField="toISICLabel" valueField="totalCount" />
                                </div>
                            </div> : <div className="na"> <LockIcon /></div>
                        :
                        (migrationSectorsHeatMap && migrationSectorsHeatMap.length) ?
                            <div className="memberMigration-heatMap">
                                <HeatMapChart
                                    name="migrationSectorsHeatMap"
                                    data={migrationSectorsHeatMap}
                                    xAxisLabels={sectorMigrationHeatMapXAxisLabels}
                                    yAxisLabels={sectorMigrationHeatMapYAxisLabels}
                                    xAxisFieldName="toISICLabel"
                                    yAxisFieldName="fromISICLabel"
                                    titleX="To Sector"
                                    titleY="From Sector"
                                    valueFieldName="totalCount"
                                    height={(sectorMigrationHeatMapYAxisLabels.length > 3) ? 150 + (sectorMigrationHeatMapYAxisLabels.length * 60) : 500}
                                    width={(sectorMigrationHeatMapXAxisLabels.length > 5) ? 150 + (sectorMigrationHeatMapXAxisLabels.length * 180) : "100%"}
                                />

                            </div> : <div className="na"><LockIcon /></div>
                    }
                </div>
            </div> : null}

            {isLoading > 4 ? <div className="occupation-tab__top-skills mb-3">
                <div className="widget-component">
                    <h4 className="border-bottom mb-2 pb-2">
                        Migration Occupations
                        {showHeatMapMigrationOccupations
                            ?
                            <RepeatIcon className="bar-toggle-icon" onClick={() => setShowHeatMapMigrationOccupations(false)} />
                            :
                            <AppsIcon className="bar-toggle-icon" onClick={() => setShowHeatMapMigrationOccupations(true)} />
                        }
                    </h4>
                    {!showHeatMapMigrationOccupations ?
                        (memberMigrationOccupations && memberMigrationOccupations.length) ?
                            <div>
                                <ExportButton countryName={countryData.country} exportDOMId="memberMigrationOccupations-diagram" urlAPI={url.exportMemberMigrationOccupations} urlDataAPI={{
                                    hubIds: [countryData.hubId]
                                }} />
                                <div id="memberMigrationOccupations-diagram">
                                    <TraceableSankyDiagram name="memberMigrationOccupations" data={memberMigrationOccupations.slice(0, 10)} fromField="fromISCO2Label" toField="toISCO2Label" valueField="totalCount" />
                                </div>
                            </div> : <div className="na"><LockIcon /></div>
                        :
                        (migrationOccupationsHeatMap && migrationOccupationsHeatMap.length) ?
                            <div className="memberMigration-heatMap">
                                <HeatMapChart
                                    name="migrationOccupationsHeatMap"
                                    data={migrationOccupationsHeatMap}
                                    xAxisLabels={occupationMigrationHeatMapXAxisLabels}
                                    yAxisLabels={occupationMigrationHeatMapYAxisLabels}
                                    xAxisFieldName="toISCO2Label"
                                    yAxisFieldName="fromISCO2Label"
                                    titleX="To Occupation"
                                    titleY="From Occupation"
                                    valueFieldName="totalCount"
                                    height={(occupationMigrationHeatMapYAxisLabels.length > 3) ? 150 + (occupationMigrationHeatMapYAxisLabels.length * 60) : 500}
                                    width={(occupationMigrationHeatMapXAxisLabels.length > 5) ? 150 + (occupationMigrationHeatMapXAxisLabels.length * 180) : "100%"}
                                />
                            </div> : <div className="na"><LockIcon /></div>
                    }
                </div>
            </div> : null}

            {/* SKILS */}
            {isLoading > 5 ? <div className="occupation-tab__top-skills mb-3">
                <div className="widget-component">
                    <h4 className="border-bottom mb-2 pb-2 occupation-tab__top-skills--header">
                        {skillType === SkillTypes.WS ?
                            <span>Top {topSkillGroup + "s"} in {titles}</span>
                            :
                            <span>Top Skills in {titles}</span>
                        }
                        {process.env.REACT_APP_ESCO_ENABLED === "true" &&
                            <Button label={skillType === SkillTypes.WS ? SkillTypes.ESCO : SkillTypes.WS} onClick={handleChangeSkillType} style={{ display: 'none' }} />
                        }
                    </h4>
                    {topSkills && topSkills.length ?
                        <div>
                            <div>
                                {skillType === SkillTypes.WS ?
                                    <>
                                        <HistoryEduIcon className="bar-toggle-icon" id="topSkillsCertification-toggle" onClick={() => setTopSkillGroup(WSSkillGroups.Certification)} />
                                        <AutoFixHighIcon className="bar-toggle-icon" id="topSkillsSoftSkill-toggle" onClick={() => setTopSkillGroup(WSSkillGroups.SoftSkill)} />
                                        <BoltIcon className="bar-toggle-icon" id="topSkillsHardSkill-toggle" onClick={() => setTopSkillGroup(WSSkillGroups.HardSkill)} />
                                    </>
                                    :
                                    <div className="d-flex justify-content-center mt-2">

                                    </div>
                                }
                            </div>
                            <ExportButton countryName={countryData.country} exportDOMId="topSkills" urlAPI={url.exportTopSkills} urlDataAPI={{
                                hubIds: [countryData.hubId],
                                sectorIds: selectedProfile.sector.length ? selectedProfile.sector.map((e: any) => e.id) : [],
                                occupationCategoryIds: selectedProfile.occupation.length ? selectedProfile.occupation.map((e: any) => e.id) : [],
                                isceD4Codes: [],
                                locations: (selectedProfile.provinces && selectedProfile.provinces.length) ? selectedProfile.provinces.map((e: any) => e.title) : [],
                                isEntryLevel: selectedProfile.isEntryLevel,
                                type: skillType === SkillTypes.WS ? topSkillGroup : SkillTypes.ESCO,
                            }} />
                            <div id={"topSkills"} >
                                <TopSkills topSkills={topSkills.find(ts => (skillType === SkillTypes.WS ? ts.skillGroup === topSkillGroup : ts.skillGroup === SkillTypes.ESCO))?.topSkills || []} skillType={skillType} />
                            </div>
                        </div> : <div className="na">N/A</div>}
                </div>
            </div> : null}

            {isLoading !== 6 ? <div><img src={LoaderImage} alt="" className="mx-auto d-block" style={{ width: 30 }} /></div> : null}
        </div>
    );
};

export default OccupationTab;
